<template>
  <div class="weetPreviewContainer">

    <!--     Name input -->
    <div class="inputLayout">
      <w-button icon="arrow-left" size="default" color="light" data-test="recorderStep2BackButton"
                :tooltip="$t('createWeetComponent.advancedEditing.tooltip.back')"
                @click="sendBack"></w-button>
      <div v-if="!isSpeach" ref="titleDiv" class="titleEditable" steptour="titleLine" :contenteditable="true"
           v-text="title"
           @focusout="changeTitleWeet" @keydown.enter="focusOutTitleEdition"/>

      <w-button v-if="isSpeach" class="headerAction" size="default"
                :enabled="isAdvancedEditingAivailable" icon="check"
                @click="informSpeach">
        {{$t('createWeetComponent.speachSave')}}
      </w-button>
    </div>

    <div class="bodyRow" :class="{withSharePanel: !isSpeach}">
      <!-- Editing tools, same floating column as the recording step -->
      <div class="previewToolBar">
        <div class="toolWithDot">
          <w-button class="toolButton" icon="message-outline" size="default" color="outlined"
                    :enabled="isAdvancedEditingAivailable"
                    :tooltip="$t('createWeetComponent.advancedEditing.button.annotation')" tooltip-position="right"
                    @click="goToAnnotationMode"/>
          <div v-if="hasAnnotationsToReview" class="reviewDot"></div>
        </div>

        <w-button class="toolButton" icon="cursor-default-click-outline" size="default" color="outlined"
                  :enabled="isAdvancedEditingAivailable"
                  :tooltip="$t('createWeetComponent.advancedEditing.button.interaction')" tooltip-position="right"
                  @click="goToInteractionMode"/>

        <w-button class="toolButton" icon="format-list-bulleted" size="default" color="outlined"
                  :enabled="isAdvancedEditingAivailable"
                  :tooltip="$t('createWeetComponent.advancedEditing.button.chapter')" tooltip-position="right"
                  @click="gotToChapterMode" v-if="!isSpeach"/>

        <w-button class="toolButton" icon="content-cut" size="default" color="outlined"
                  :enabled="isAdvancedEditingAivailable"
                  :tooltip="$t('createWeetComponent.advancedEditing.button.cut')" tooltip-position="right"
                  @click="gotToCutMode"/>

        <w-button class="toolButton" icon="subtitles-outline" size="default" color="outlined"
                  :enabled="isAdvancedEditingAivailable"
                  :tooltip="$t('createWeetComponent.advancedEditing.button.subtitles')" tooltip-position="right"
                  @click="goToSubtitleMode" v-if="subtitleFeatureAvailable"/>

        <w-button class="toolButton" icon="microphone-outline" size="default" color="outlined"
                  :enabled="isAdvancedEditingAivailable"
                  :tooltip="$t('createWeetComponent.advancedEditing.button.voiceOver')" tooltip-position="right"
                  @click="gotToVoiceOverMode"/>

        <w-button class="toolButton" icon="image-outline" size="default" color="outlined"
                  :enabled="isAdvancedEditingAivailable"
                  :tooltip="$t('createWeetComponent.advancedEditing.button.thumbnail')" tooltip-position="right"
                  @click="gotToThumbnailMode" v-if="!isSpeach"/>
      </div>

      <!-- keeps the subtitle navigation and its waiting modal, driven from the tool above -->
      <GoToSubtitleWeetButton ref="subtitleButton" v-show="false" :weet="myWeet"
                              v-if="subtitleFeatureAvailable"/>

      <div class="playerZone">
        <div class="playerContainer" ref="playerPreviewScreen">
          <WeetPlayer v-if="!isProcessing" :miniSticker="true" :editMode="true" :maxHeight="0" :fullResponsive="true"
                      :emote-aivailable="false"
                      :fullScreenElement="'playerPreviewScreen'"
                      :weetID="weetID"/>
          <div v-else class="progressWeet">
            <ProgressProcessPlayer :weet="myWeet"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {Component, Emit, Prop, Vue, Watch} from 'vue-property-decorator';
import WeetPlayer from '@/components/play/player/WeetPlayer.vue';
import store from '@/store';
import WInput from '@/components/wrapper/w-input.vue';
import {DUPLICATE_MY_WEET, SEND_CHANGE_WEET_TITLE} from '@/store/myWeet/myWeetAction';
import WError from '@/components/wrapper/w-error.vue';
import {Weet} from '@/store/weet/weetModel';
import WButton from '@/components/wrapper/w-button.vue';
import {copyLink, createUnsavedEditingWeet, goToSubtitleService, isWeetEquals, isWeetIsReady} from '@/utils/weetUtil';
import {inform} from '@/utils/dialog';
import EndWeetButton from '@/components/actionButton/EndWeetButton.vue';
import {setActionEvent, setPageEvent} from '@/utils/tracker';
import {ActionEvent, PageEvent} from '@/enum/TrackerEnum';
import ProgressProcessPlayer from '@/components/play/process/ProgressProcessPlayer.vue';
import ChannelWorkspace from '@/components/channel/ChannelWorkspace.vue';
import ChannelWorkspaceAddDropDown from '@/components/channel/ChannelWorkspaceAddDropDown.vue';
import {AdvancedEditingMode} from "@/enum/AdvancedEditingMode";
import GoToSubtitleWeetButton from "@/components/actionButton/GoToSubtitleWeetButton.vue";
import {isSpeach} from "@/utils/speachUtils";
import WIcon from '@/components/wrapper/w-icon.vue';


@Component({
  components: {
    WIcon,
    GoToSubtitleWeetButton,
    ChannelWorkspaceAddDropDown,
    ChannelWorkspace, ProgressProcessPlayer, EndWeetButton, WButton, WError, WInput, WeetPlayer
  },
})
export default class CreateWeetPreviewStep extends Vue {
  private errormsg: string = '';
  private title: string = '';
  private loading: boolean = false;

  get isSpeach() {
    return isSpeach();
  }

  get hasAnnotationsToReview(): boolean {
    return store.getters.getAnnotationsToReviewCount > 0;
  }

  get isAdvancedEditingAivailable(): boolean {
    return (!store.getters.isSychronisationInProgress && !store.getters.timelineNeedToBeSaved);
  }

  get subtitleFeatureAvailable(){
    return store.getters.isTranscript;
  }

  get weetID(): string {
    return store.getters.getEditingWeetID;
  }

  get selectedWorkspaceID(): string {
    return store.getters.getSelectedWorkspaceID;
  }

  get isProcessing(): boolean {
    return !isWeetIsReady(this.myWeet);
  }

  get myWeet(): Weet {
    return createUnsavedEditingWeet(this.weetID);
  }

  private clickEnd() {
    this.loading = true;
    this.$emit('end');
  }

  private sendBack() {
    this.$emit('back');
  }


  private gotToChapterMode(value: string) {
    setActionEvent(ActionEvent.record_advanced_setting);
    this.$emit('advancedEditing', AdvancedEditingMode.CHAPTER);
  }

  private goToInteractionMode(value: string) {
    setActionEvent(ActionEvent.record_advanced_setting);
    this.$emit('advancedEditing', AdvancedEditingMode.INTERACTION);
  }
  private goToAnnotationMode(value: string) {
    setActionEvent(ActionEvent.record_advanced_setting);
    this.$emit('advancedEditing', AdvancedEditingMode.ANNOTATION);
  }

  private gotToCutMode(value: string) {
    setActionEvent(ActionEvent.record_advanced_setting);
    this.$emit('advancedEditing', AdvancedEditingMode.CUT);
  }

  private gotToThumbnailMode(value: string) {
    setActionEvent(ActionEvent.record_advanced_setting);
    this.$emit('advancedEditing', AdvancedEditingMode.THUMBNAIL);
  }

  private gotToVoiceOverMode(value: string) {
    setActionEvent(ActionEvent.record_advanced_setting);
    this.$emit('advancedEditing', AdvancedEditingMode.VOICE_OVER);
  }

  private goToSubtitleMode() {
    // @ts-ignore
    this.$refs.subtitleButton.waitAndGoToSubtitleService();
  }


  @Emit('informSpeach')
  private informSpeach(){
    return;
  }

  private mounted() {
    const weet = createUnsavedEditingWeet(this.weetID);
    if (weet !== null && weet) {
      this.title = weet.title;
    }
    setPageEvent(PageEvent.page_record_step_share_preview);
    this.selectTitle();
  }


  private selectTitle() {
    const titleDiv = this.$refs.titleDiv as HTMLDivElement;
    if (titleDiv) {
      titleDiv.focus();
      // @ts-ignore
      window.getSelection().selectAllChildren(titleDiv);
    }
  }

  private focusOutTitleEdition(event: InputEvent) {
    if (event.target != null) {
      const element = event.target as HTMLElement;
      element.blur();
    }
  }

  private changeTitleWeet(event: InputEvent) {
    if (event.target != null) {
      const element = event.target as HTMLElement;
      if (element.innerText.trim() !== this.title.trim()) {
        // get weetID
        store.dispatch(SEND_CHANGE_WEET_TITLE,
            {
              weetID: store.getters.getEditingWeetID,
              title: element.innerText,
            }).then(() => {
          this.errormsg = '';
        }).catch((err) => {
          this.errormsg = err;
        });
        inform(this.$t('player.title.edition.success').toString());
      }
    }
  }
}

</script>

<style scoped lang="scss">

.weetPreviewContainer {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--light);

  .bodyRow {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: row;

    // the share panel sits on the right: 320px plus its 16px margin
    &.withSharePanel {
      padding-right: 336px;
    }
  }

  // Editing tools: invisible column, floating buttons, like the recording step
  .previewToolBar {
    flex: none;
    width: 68px;
    padding-left: 16px;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 12px;
    background: transparent;

    // square icon button: colours and hover are the button component's job
    .toolButton ::v-deep .button {
      width: 52px;
      padding-left: 0;
      padding-right: 0;
    }

    .toolWithDot {
      position: relative;

      .reviewDot {
        position: absolute;
        top: 4px;
        right: 4px;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: #FF4444;
        pointer-events: none;
      }
    }
  }

  .playerZone {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    container-type: size;
  }

  .playerContainer {
    position: relative;
    margin: auto;
    //aspect-ratio: 16 / 9;
    // biggest 16/9 box fully visible in the zone, 16px of breathing room
    width: min(calc(100cqw - 32px), calc((100cqh - 32px) * 16 / 9));
    background: var(--black);
    border-radius: 8px;

    .progressWeet {
      display: flex;
      justify-items: center;
      align-content: center;
      height: 100%;
      width: 100%;
      padding: 64px;
    }
  }

  .buttonValidate {
    margin-left: 16px;
  }

  .inputLayout {
    display: flex;
    align-items: center;
    text-align: left;
    flex: none;
    width: 100%;
    height: 72px;
    padding: 0 24px;
    background: var(--white);
    box-shadow: 0 1px 4px rgba(10, 8, 47, 0.08);
    z-index: 10;

    .titleEditable {
      font-size: 20px;
      font-weight: 900;
      flex-grow: 1;
      color: var(--black);
      text-align: left;
      line-height: 28px;
      margin-left: 16px;
      padding: 2px 4px;
      border-radius: 4px;

      &:hover {
        box-shadow: 0px 0px 2px var(--light2);
      }

      &:focus {
        box-shadow: 0px 0px 2px var(--primary);
      }

    }

    // pushed right even when the title is hidden (Speach mode)
    .headerAction {
      margin-left: auto;
    }

    .errorMessage {
      margin-top: 16px;
      margin-bottom: 16px;
    }

  }
}
</style>
